import classNames from 'classnames';
import { Icon, IconName } from '@components/icon';
import { Body } from '@components/text';
import './PillBadge.scss';

export const PILL_BADGE_COLOR_SCHEMES = [
  'primary',
  'secondary',
  'tertiary',
  'brand',
  'brand-inverse',
  'promo',
  'critical',
] as const;
export const PILL_BADGE_SIZES = ['large', 'small'] as const;

export type PillBadgeColorScheme = (typeof PILL_BADGE_COLOR_SCHEMES)[number];
export type PillBadgeSize = (typeof PILL_BADGE_SIZES)[number];

export interface PillBadgeProps {
  /** id to reference the element */
  id?: string;
  /** Color scheme. Default primary */
  colorScheme?: PillBadgeColorScheme;
  /** Change size of layout for desktop and mobile. Default large */
  size?: PillBadgeSize;
  /** String for text */
  label?: string;
  /** Icon name for icon type */
  icon?: IconName;
  /** Optional aria label for accessibility */
  ariaLabel?: string;
  /** Optional classname for the component */
  className?: string;
}

/**
 * PillBadge component
 * @param id - Id to reference the element.
 * @param colorScheme - Color scheme. Default primary.
 * @param size - change size of layout for desktop and mobile
 * @param label - String for text.
 * @param icon - Icon name for icon type.
 * @param ariaLabel - Optional aria label for accessibility for icon.
 * @param className - Optional classname for the component.
 */
export const PillBadge: React.FC<PillBadgeProps> = ({
  id,
  colorScheme = 'primary',
  size = 'large',
  label,
  icon,
  ariaLabel,
  className,
  ...rest
}) => {
  const isIconOnly = icon && !label;
  const isTextOnly = !icon && label;

  return (
    <div
      id={id}
      className={classNames(
        'vds-pill-badge',
        `vds-pill-badge--${colorScheme}`,
        { 'vds-pill-badge--icon-only': size === 'large' && isIconOnly },
        { 'vds-pill-badge--text-only': size === 'large' && isTextOnly },
        { 'vds-pill-badge--small': size == 'small' },
        className,
      )}
      aria-label={ariaLabel}
      {...rest}>
      <Body
        variant="caption"
        size={size == 'small' || isTextOnly ? 'small' : 'large'}
        weight="semibold"
        align="center"
        className={classNames('vds-pill-badge__label', {
          'vds-pill-badge__label--icon-only': size === 'large' && isIconOnly,
        })}>
        {size === 'large' && icon && <Icon name={icon} size="small" className="icon" />}
        {label}
      </Body>
    </div>
  );
};
